import { Box, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BuildIcon from "@material-ui/icons/Build";
import {
	CalendarEntry,
	getCalendarEntryViewKey,
	getScheduleEntryTypeIds,
} from "Components/Calendar/CalendarEntryHelper";
import CalendarGroupRows from "Components/Calendar/CalendarGroupRows";
import {
	CellSize,
	getSplittedScheduleEntryTypeIds,
	getSplittedScheduleGroupIds,
	useIsCalendarSubHeadOpen,
} from "Components/Calendar/CalendarHelper";
import CalendarMemberRows from "Components/Calendar/CalendarMemberRows";
import CalendarNameColumn from "Components/Calendar/CalendarNameColumn";
import CalendarResourceRows from "Components/Calendar/CalendarResourceRows";
import CalendarRootView from "Components/Calendar/CalendarRootView";
import CalendarRow from "Components/Calendar/CalendarRow";
import CalendarSchedulePhaseRows, {
	CalendarSchedulePhaseRow,
} from "Components/Calendar/CalendarSchedulePhaseRows";
import CalendarDayDropAreaView from "Components/CalendarDay/CalendarDayDropAreaView";
import CalendarDayEntryView from "Components/CalendarDay/CalendarDayEntryView";
import CurrentTimeIndicator from "Components/CalendarDay/CurrentTimeIndicator";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { useCompanySetting } from "Components/CompanySetting/hooks/useCompanySetting";
import {
	CalendarEntryUpdate,
	getAcceptedDropTypes,
	getDropAreaViewKey,
	getDayCalendarEntriesDropAreas,
} from "Components/DropArea/DropAreaHelper";
import {
	ValidMember,
	displayEmployeeName,
} from "Components/Members/MembersHelper";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { getIsMultiWeekEnabled } from "Components/ProfileLimit/helpers/getIsMultiWeekEnabled";
import { useProfileLimits } from "Components/ProfileLimit/hooks/useProfileLimits";
import { useProjects } from "Components/Project/hooks/useProjects";
import {
	getDayScheduleEntriesResizePixelSet,
	getScheduleEntryResizePixelSet,
	getSchedulePhaseResizePixelSet,
	getDayBlockingResizePixel,
} from "Components/Resize/ResizeHelper";
import { useSchedule } from "Components/Schedule/hooks/useSchedule";
import { useDayScheduleEntries } from "Components/ScheduleEntry/hooks/useDayScheduleEntries";
import { useScheduleEntryModal } from "Components/ScheduleEntry/hooks/useScheduleEntryModal";
import { useScheduleEntryTypes } from "Components/ScheduleEntryType/hooks/useScheduleEntryTypes";
import { getPartialSchedulePhaseFromCalendarEntry } from "Components/SchedulePhase/SchedulePhaseHelper";
import { useSchedulePhaseModal } from "Components/SchedulePhase/hooks/useSchedulePhaseModal";
import { useSchedulePhases } from "Components/SchedulePhase/hooks/useSchedulePhases";
import { createScheduleResource } from "Components/ScheduleResource/ScheduleResourceHelper";
import { useScheduleResourceModal } from "Components/ScheduleResource/hooks/useScheduleResourceModal";
import { getFirstPaidDay } from "Components/ProfileLimit/helpers/getFirstPaidDay";
import { useTutorialModal } from "Components/Tutorial/hooks/useTutorialModal";
import { useVisibleMinutes } from "Components/WorkingHours/hooks/useVisibleMinutes";
import { MinuteRange } from "Components/WorkingHours/types/MinuteRange";
import { color } from "Configs/theme";
import {
	FALLBACK_DROP_AREA_COLOR_HEX,
	getColorHex,
	getColorHexFromDropArea,
	getColorHexFromScheduleEntry,
} from "Helpers/ColorHelper";
import { getCalendarEntryViewTooltipText } from "Helpers/TooltipHelper";
import { useApp } from "Providers/AppProvider";
import ErrorView from "Views/ErrorView";
import LoadingIndicator from "Views/LoadingIndicator";
import MainActionButton from "Views/MainActionButton";
import { Project, ScheduleResource } from "domain-entities";
import { useSnackbar } from "material-ui-snackbar-provider";
import moment from "moment";
import React, { FC, Fragment } from "react";
import { v4 } from "uuid";
import {
	getIsDaySingleRow,
	getDayCalendarEntries,
	getDaySchedulePhasesResizePixelSet,
	isDropAreaOverlappingHour,
} from "./CalendarDayHelper";
import { DropArea } from "Components/DropArea/types/DropArea";

function getHoursArray(workingMinutes: MinuteRange) {
	const startHour = Math.floor(workingMinutes.start / 60);
	const endHour = Math.ceil(workingMinutes.end / 60);
	return Array(endHour - startHour)
		.fill(null)
		.map((_, index) => startHour + index);
}

interface Props {
	cellSize: CellSize;
}

const CalendarDayView: FC<Props> = (props) => {
	const isInternalAdmin = useIsInternalAdmin();
	const companySettingStatuses = useCompanySetting(
		(state) => state.item?.status
	);
	const selectedDate = useCalendar((state) => state.selectedDate);
	const workingMinutes = useCalendar((state) => state.workingMinutes);
	const hours = getHoursArray(workingMinutes);
	const { isNative } = useApp();
	const profileLimits = useProfileLimits((state) => state.items);
	const scheduleEntries = useDayScheduleEntries((state) => state.items);
	const visibleMinutes = useVisibleMinutes();
	const scheduleEntryTypes = useScheduleEntryTypes((state) => state.items);
	const isInitializingScheduleEntryTypes = useScheduleEntryTypes(
		(state) => state.isInitializing
	);
	const scheduleEntryTypesError = useScheduleEntryTypes((state) => state.error);
	const projects = useProjects((state) => state.projects);
	const cellSize = props.cellSize;
	const firstPaidDay = getFirstPaidDay();
	const classes = useStyles({
		hourWidth: cellSize.day.hourWidth,
		nameWidth: cellSize.day.nameWidth,
		hoursLength: hours.length
	});

	const snackbar = useSnackbar();
	const scheduleGroups = useSchedule((state) => state.item?.groups);
	const { isCalendarSubHeadOpen, setIsCalendarSubHeadOpen } =
		useIsCalendarSubHeadOpen();
	const endOfDay = selectedDate.clone().add(1, "day");

	const schedulePhasesResizePixelSet = getDaySchedulePhasesResizePixelSet(
		selectedDate,
		cellSize
	);

	const scheduleEntriesResizePixelSet = getDayScheduleEntriesResizePixelSet(
		visibleMinutes,
		selectedDate,
		cellSize
	);

	const dayCalendarEntries = getDayCalendarEntries(
		scheduleEntries,
		selectedDate
	);

	const { memberScheduleEntryTypeIds, resourceScheduleEntryTypeIds } =
		getSplittedScheduleEntryTypeIds(scheduleEntryTypes);

	const { scheduleGroupMemberIds, scheduleGroupResourceIds } =
		getSplittedScheduleGroupIds(scheduleGroups);

	function renderSchedulePhaseDropArea(
		dropArea: DropArea,
		colorHex: string,
		project?: Project
	) {
		return (
			<CalendarDayDropAreaView
				dropArea={dropArea}
				cellSize={cellSize}
				visibleMinutes={visibleMinutes}
				scheduleEntryTypeIndex={-1}
				colorHex={colorHex}
				acceptedDropTypes={[]}
				isFullDay
				onClick={() =>
					useSchedulePhaseModal.getState().openWithItem(
						{
							project,
							forSchedulePhase: {
								start: dropArea.start,
								end: dropArea.end,
							},
						},
						true
					)
				}
			/>
		);
	}

	function renderEmptySchedulePhaseRow() {
		if (!isInternalAdmin) return [null];

		const dropArea = {
			start: selectedDate.clone().startOf('day'),
			end: selectedDate.clone().endOf('day')
		};

		return (
			<Box
				key="fullday"
				className={classes.fullDayColumn}
			>
				{renderSchedulePhaseDropArea(
					dropArea,
					FALLBACK_DROP_AREA_COLOR_HEX
				)}
			</Box>
		);
	}

	function getDayColumnClass(hour: moment.Moment) {
		const hourValue = hour.hours();
		const isInWorkingHours = hourValue >= Math.floor(workingMinutes.start / 60) &&
			hourValue < Math.ceil(workingMinutes.end / 60);
		return isInWorkingHours ? classes.hourColumn : `${classes.hourColumn} ${classes.nonWorkingHourColumn}`;
	}

	function renderSchedulePhaseRow(row: CalendarSchedulePhaseRow) {
		const colorHex = getColorHex(row.project.colorTag);

		return (
			<Box
				key="fullday"
				className={classes.fullDayColumn}
			>
				{isInternalAdmin &&
					renderSchedulePhaseDropArea(
						{
							start: selectedDate.clone().startOf('day'),
							end: selectedDate.clone().endOf('day')
						},
						colorHex,
						row.project
					)
				}

				{row.items.map((rowItem) => {
					const schedulePhaseResizePixelSet = getSchedulePhaseResizePixelSet(
						schedulePhasesResizePixelSet,
						row.items.map((item) => item.schedulePhase),
						rowItem.schedulePhase
					);

					return (
						<CalendarDayEntryView
							key={rowItem.calendarEntry.id}
							calendarEntry={{
								...rowItem.calendarEntry,
								startTime: selectedDate.clone().startOf('day').unix(),
								endTime: selectedDate.clone().endOf('day').unix()
							}}
							originalCalendarEntry={rowItem.calendarEntry}
							onResizeCalendarEntry={async (calendarEntry) => {
								try {
									await useSchedulePhases
										.getState()
										.updateItem(
											getPartialSchedulePhaseFromCalendarEntry(calendarEntry)
										);
								} catch (error) {
									snackbar.showMessage(verifyError(error).message);
								}
							}}
							colorHex={colorHex}
							scheduleEntryTypeIndex={-1}
							projects={projects}
							cellSize={cellSize}
							hour={0}
							visibleMinutes={visibleMinutes}
							resizePixelSet={schedulePhaseResizePixelSet}
							dropType="project"
							selectCalendarEntry={() =>
								useSchedulePhaseModal.getState().openWithItem({
									project: row.project,
									schedulePhase: rowItem.schedulePhase,
								})
							}
							isFullDay
							isNew
							tooltip={getCalendarEntryViewTooltipText({
								projectName: projects.find(
									(project) => project.id === rowItem.schedulePhase.projectId
								)?.name,
								phaseStatusName:
									companySettingStatuses?.find(
										(status) => status.id === rowItem.schedulePhase.statusId
									)?.name || "-",
							})}
						/>
					);
				})}
			</Box>
		);
	}

	function renderScheduleEntryColumns(
		calendarEntries: CalendarEntry[],
		scheduleEntryTypeIds: string[],
		categoryScheduleEntryTypeIds: string[],
		calendarEntryUpdate: CalendarEntryUpdate,
		dropAreas: DropArea[],
		isSingleRow: boolean,
		isLimitEnabled?: boolean
	) {
		return hours.map((hour, index) => {
			const currentHour = selectedDate.clone().set('hour', hour);
			const hourClass = getDayColumnClass(currentHour);
			const isPaidColumn =
				!isLimitEnabled && currentHour.isSameOrAfter(firstPaidDay);
			const isFirstPaidColumn =
				!isLimitEnabled &&
				(currentHour.isSame(firstPaidDay) || (isPaidColumn && index === 0));

			const hourCalendarEntries = isPaidColumn
				? []
				: calendarEntries.filter((scheduleEntry) => {
					const startTime = moment.unix(scheduleEntry.startTime);
					const startOfDay = selectedDate.clone().startOf('day');
					const firstVisibleHour = Math.floor(visibleMinutes.start / 60);
					const startOfCurrentHour = currentHour;
					const endOfCurrentHour = startOfCurrentHour.clone().add(1, 'hour');

					if (startTime.isBefore(startOfDay)) {
						return hour === firstVisibleHour;
					}

					return startTime.isSameOrAfter(startOfCurrentHour) && startTime.isBefore(endOfCurrentHour);
				});

			const hourDropAreas = isPaidColumn
				? []
				: dropAreas.filter((dropArea) =>
						isDropAreaOverlappingHour(currentHour, dropArea)
				  );

			return (
				<Box key={hour} className={hourClass}>
					{hourDropAreas.map((dropArea) => {
						const colorHex = getColorHexFromDropArea(
							dropArea,
							scheduleEntryTypes
						);

						const scheduleEntryTypeIndex = scheduleEntryTypeIds.findIndex(
							(typeId) => typeId === dropArea.typeId
						);

						const acceptedDropTypes = getAcceptedDropTypes(
							dropArea,
							scheduleEntryTypeIds,
							categoryScheduleEntryTypeIds
						);

						return (
							<CalendarDayDropAreaView
								key={`${getDropAreaViewKey(dropArea, acceptedDropTypes)}`}
								dropArea={dropArea}
								cellSize={cellSize}
								visibleMinutes={visibleMinutes}
								scheduleEntryTypeIndex={scheduleEntryTypeIndex}
								colorHex={colorHex}
								acceptedDropTypes={acceptedDropTypes}
								isSingleRow={isSingleRow}
								calendarEntryUpdate={calendarEntryUpdate}
								updateScheduleEntry={async (scheduleEntry) => {
									try {
										await useDayScheduleEntries.getState().updateItem({
											...scheduleEntry,
											...calendarEntryUpdate,
											startTime: dropArea.start.unix(),
											endTime: dropArea.end.unix(),
										});
									} catch (error) {
										snackbar.showMessage(verifyError(error).message);
									}
								}}
								addScheduleEntry={async (scheduleEntry) => {
									try {
										await useDayScheduleEntries.getState().setItem({
											...scheduleEntry,
											...calendarEntryUpdate,
											id: v4(),
											startTime: dropArea.start.unix(),
											endTime: dropArea.end.unix(),
										});

										setTimeout(() => {
											useTutorialModal.getState().openWithItem("resize");
										}, 200);
									} catch (error) {
										snackbar.showMessage(verifyError(error).message);
									}
								}}
								onClick={() =>
									useScheduleEntryModal.getState().openWithItem(
										{
											id: v4(),
											typeId: dropArea.typeId,
											startTime: dropArea.start.unix(),
											endTime: dropArea.end.unix(),
											...calendarEntryUpdate,
										},
										true
									)
								}
							/>
						);
					})}
					{hourCalendarEntries.map((item) => {
						const blockingResizePixel = getDayBlockingResizePixel(
							item,
							calendarEntries,
							visibleMinutes,
							cellSize
						);

						const resizePixelSet = getScheduleEntryResizePixelSet(
							scheduleEntriesResizePixelSet,
							blockingResizePixel
						);

						const scheduleEntryTypeIndex = scheduleEntryTypeIds.findIndex(
							(typeId) => typeId === item.typeId
						);

						const colorHex = getColorHexFromScheduleEntry(
							item,
							projects,
							scheduleEntryTypes
						);

						return (
							<CalendarDayEntryView
								key={getCalendarEntryViewKey(item, isSingleRow)}
								calendarEntry={item}
								onResizeCalendarEntry={async (scheduleEntry) => {
									try {
										await useDayScheduleEntries
											.getState()
											.updateItem(scheduleEntry);
									} catch (error) {
										snackbar.showMessage(verifyError(error).message);
									}
								}}
								colorHex={colorHex}
								scheduleEntryTypeIndex={scheduleEntryTypeIndex}
								projects={projects}
								cellSize={cellSize}
								hour={hour}
								visibleMinutes={visibleMinutes}
								resizePixelSet={resizePixelSet}
								dropType={item.typeId || "project"}
								selectCalendarEntry={() =>
									useScheduleEntryModal.getState().openWithItem(item)
								}
								isSingleRow={isSingleRow}
								tooltip={getCalendarEntryViewTooltipText({
									projectName: projects.find(
										(project) => project.id === item.projectId
									)?.name,
									entryTypeName: scheduleEntryTypes.find(
										({ id }) => id === item.typeId
									)?.name,
								})}
							/>
						);
					})}
					{isFirstPaidColumn && (
						<Box
							style={{
								backgroundColor: "rgba(0, 0, 0, 0.12)",
								position: "absolute",
								top: 0,
								bottom: 0,
								left: 0,
								right: 0,
								zIndex: 10,
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}
						>
							<Tooltip title="Upgrade to access this feature" placement="top">
								<div style={{ color: "#555", fontWeight: "bold" }}>PRO</div>
							</Tooltip>
						</Box>
					)}
				</Box>
			);
		});
	}

	function renderMemberRow(member: ValidMember) {
		const calendarEntries = dayCalendarEntries.filter(
			(item) => item.userId === member.id
		);

		const isSingleRow = getIsDaySingleRow(calendarEntries);
		const scheduleEntryTypeIds = getScheduleEntryTypeIds(calendarEntries);
		const calendarEntryUpdate: CalendarEntryUpdate = {
			userId: member.id,
			resourceId: undefined,
		};

		const dropAreas = isInternalAdmin
			? getDayCalendarEntriesDropAreas(
					selectedDate,
					calendarEntries,
					scheduleEntryTypeIds,
					visibleMinutes,
					workingMinutes
			  )
			: [];

		const isLimitEnabled = getIsMultiWeekEnabled(member.id, profileLimits);
		const employeeName = displayEmployeeName(member);

		return (
			<CalendarRow
				key={member.id}
				scheduleEntryTypesLength={scheduleEntryTypeIds.length}
				isSingleRow={isSingleRow}
				renderColumns={() => (
					<Fragment>
						<CalendarNameColumn width={props.cellSize.day.nameWidth}>
							<Tooltip title={employeeName || ""} placement="top">
								<div>{employeeName}</div>
							</Tooltip>
						</CalendarNameColumn>
						{renderScheduleEntryColumns(
							calendarEntries,
							scheduleEntryTypeIds,
							memberScheduleEntryTypeIds,
							calendarEntryUpdate,
							dropAreas,
							isSingleRow,
							isLimitEnabled
						)}
					</Fragment>
				)}
			/>
		);
	}

	function renderResourceRow(resource: ScheduleResource) {
		const calendarEntries = dayCalendarEntries.filter(
			(item) => item.resourceId === resource.id
		);

		const isSingleRow = getIsDaySingleRow(calendarEntries);
		const scheduleEntryTypeIds = getScheduleEntryTypeIds(calendarEntries);
		const calendarEntryUpdate: CalendarEntryUpdate = {
			resourceId: resource.id,
			userId: undefined,
		};

		const dropAreas = isInternalAdmin
			? getDayCalendarEntriesDropAreas(
					selectedDate,
					calendarEntries,
					scheduleEntryTypeIds,
					visibleMinutes,
					workingMinutes
			  )
			: [];

		return (
			<CalendarRow
				key={resource.id}
				scheduleEntryTypesLength={scheduleEntryTypeIds.length}
				isSingleRow={isSingleRow}
				renderColumns={() => (
					<Fragment>
						<CalendarNameColumn
							width={props.cellSize.day.nameWidth}
							isEditable={isInternalAdmin}
							onClickEdit={() =>
								useScheduleResourceModal.getState().openWithItem(resource)
							}
						>
							<Tooltip title={resource.name || ""} placement="top">
								<div>
									<BuildIcon />
									{resource.name}
								</div>
							</Tooltip>
						</CalendarNameColumn>
						{renderScheduleEntryColumns(
							calendarEntries,
							scheduleEntryTypeIds,
							resourceScheduleEntryTypeIds,
							calendarEntryUpdate,
							dropAreas,
							isSingleRow,
							true
						)}
					</Fragment>
				)}
			/>
		);
	}

	function renderRows() {
		if (isInitializingScheduleEntryTypes) return <LoadingIndicator />;
		if (scheduleEntryTypesError) {
			return <ErrorView error={scheduleEntryTypesError} />;
		}
		return (
			<Fragment>
				<CalendarSchedulePhaseRows
					startOfCalendar={selectedDate}
					endOfCalendar={endOfDay}
					nameWidth={props.cellSize.day.nameWidth}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderEmptyRow={renderEmptySchedulePhaseRow}
					renderRow={renderSchedulePhaseRow}
				/>
				<CalendarGroupRows
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderMemberRow={renderMemberRow}
					renderResourceRow={renderResourceRow}
				/>
				<CalendarMemberRows
					scheduleGroupMemberIds={scheduleGroupMemberIds}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderMemberRow={renderMemberRow}
				/>
				<CalendarResourceRows
					scheduleGroupResourceIds={scheduleGroupResourceIds}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderResourceRow={renderResourceRow}
				/>
			</Fragment>
		);
	}

	return (
		<Fragment>
			<CalendarRootView cellSize={props.cellSize}>
				{renderRows()}
				<CurrentTimeIndicator cellSize={props.cellSize} />
			</CalendarRootView>
			{isInternalAdmin && !isNative && (
				<MainActionButton
					openSchedulePhaseModal={() => {
						const startDay =
							selectedDate.isSame(moment(), "day") ? moment() : selectedDate.clone();

						useSchedulePhaseModal.getState().openWithItem(
							{
								forSchedulePhase: {
									start: startDay.clone(),
									end: startDay.clone(),
								},
							},
							true
						);
					}}
					openScheduleResourceModal={() => {
						useScheduleResourceModal
							.getState()
							.openWithItem(createScheduleResource(), true);
					}}
				/>
			)}
		</Fragment>
	);
};

export default CalendarDayView;

interface StyleProps {
	nameWidth: number;
	hourWidth: number;
	hoursLength: number;
}

const useStyles = makeStyles({
	hourColumn: (props: StyleProps) => ({
		display: "flex",
		position: "relative",
		borderLeft: `1px solid ${color.tableCellBorder}`,
		borderBottom: `1px solid ${color.tableCellBorder}`,
		width: `calc((100% - ${props.nameWidth}px) / ${props.hoursLength})`,
		minWidth: "60px",
	}),
	nonWorkingHourColumn: {
		backgroundColor: color.tableHeadBackground,
	},
	fullDayColumn: {
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		position: "relative",
		borderLeft: `1px solid ${color.tableCellBorder}`,
		borderBottom: `1px solid ${color.tableCellBorder}`,
		width: (props: StyleProps) => `calc(100% - ${props.nameWidth}px - 1px)`,
	},
});
